// This store is like onboardingTags, but specific to the more complex nature of the /onboarding/tags/overview page,
// encompassing both the split of permissions and folder management. Putting it in state so as to allow modals to interact with it too.
import Network from 'lib/api/network';

export default {
  namespaced: true,

  state() {
    return {
      editableTags: [],
      uneditableTags: [],
      allFolders: [],
      showUneditableTags: false,
      showArchivedTagsLink: false,
    };
  },

  getters: {
    editableTags(state) { return [...state.editableTags].sort((a, b) => a.name.localeCompare(b.name)); },
    uneditableTags(state) { return [...state.uneditableTags].sort((a, b) => a.name.localeCompare(b.name)); },
    allTags(state) { return state.editableTags.concat(state.uneditableTags); },
    editableUnfolderedTags(state) { return state.editableTags.filter(t => !t.folder_id); },
    tagFolders(state) { return [...state.allFolders].sort((a, b) => a.name.localeCompare(b.name)); },
    tagsForFolder: (state) => (folderId) => state.editableTags.filter(t => t.folder_id === folderId),
  },

  mutations: {
    updateTag(state, { tagId, key, value }) {
      const tag = state.editableTags.find(t => t.id === tagId);
      if (tag) tag[key] = value;
    },

    upsertFolder(state, { folder, tagIds }) {
      const folderIdx = state.allFolders.findIndex(f => f.id === folder.id);
      // First make all cosmetic changes to the folder, either by adjusting an existing one or creating a new one.
      if (folderIdx > -1) {
        state.allFolders[folderIdx] = folder;
      } else {
        state.allFolders.push(folder);
      }
      // Then, iterate through all the tags in editableTags and update their folder_values based on our new tagIds array.
      state.editableTags.forEach((tag) => {
        if (tag.folder_id === folder.id && !tagIds.includes(tag.id)) { // This tag is currently in the folder, but we just removed it
          tag.folder_id = null;
        } else if (tag.folder_id !== folder.id && tagIds.includes(tag.id)) { // This tag is not currently in the folder, but we just added it
          tag.folder_id = folder.id;
        }
      });
    },

    deleteFolder(state, { folder }) {
      // First, remove the folder from the list
      state.allFolders = state.allFolders.filter(f => f.id !== folder.id);
      // Then, iterate through all the tags in editableTags and update their folder_values based on our new tagIds array.
      state.editableTags.forEach((tag) => {
        if (tag.folder_id === folder.id) tag.folder_id = null;
      });
    },
  },

  actions: {
    removeTagFromFolder({ state, getters, commit, dispatch }, tag) {
      // Assemble params
      const folder = state.allFolders.find(f => f.id === tag.folder_id);
      const tagIds = getters.tagsForFolder(folder.id).map(t => t.id).filter(id => id !== tag.id);
      const params = { id: folder.id, tag_ids: tagIds };
      // Optimtic update
      commit('updateTag', { tagId: tag.id, key: 'folder_id', value: null });
      // Dispatch action
      return dispatch('upsertFolder', params)
        .then(() => { dispatch('setSuccessToast', `${tag.name} removed from ${folder.name}`, { root: true }); })
        .catch(() => {
          dispatch('setErrorToast', `Could not remove ${tag.name} from ${folder.name}`, { root: true });
          commit('updateTag', { tagId: tag.id, key: 'folder_id', value: folder.id });
        });
    },

    addTagToFolder({ getters, commit, dispatch }, { tag, folder }) {
      // Assemble params
      const tagIds = [...getters.tagsForFolder(folder.id), tag].map(t => t.id);
      const params = { id: folder.id, tag_ids: tagIds };
      // Optimistic update
      commit('updateTag', { tagId: tag.id, key: 'folder_id', value: folder.id });
      // Dispatch action
      return dispatch('upsertFolder', params)
        .then(() => { dispatch('setSuccessToast', `${tag.name} added to ${folder.name}`, { root: true }); })
        .catch(() => {
          dispatch('setErrorToast', `Could not add ${tag.name} to ${folder.name}`, { root: true });
          commit('updateTag', { tagId: tag.id, key: 'folder_id', value: null });
        });
    },

    submitFolderEditModal({ dispatch, commit }, folderParams) {
      commit('update', { key: 'formSubmitted', value: true }, { root: true });
      // Params already assembled, and no need to do optimistic update because modal won't close until it's done.
      return dispatch('upsertFolder', folderParams)
        .then(({ folder, tagIds }) => commit('upsertFolder', { folder, tagIds }))
        .catch(() => dispatch('setErrorToast', 'Uh-oh, there was an error saving this folder. Try again.', { root: true }))
        .finally(() => {
            commit('update', { key: 'formSubmitted', value: false }, { root: true });
            dispatch('closeModal', null, { root: true });
          });
    },

    upsertFolder(_store, folderParams) {
      return new Promise((resolve, reject) => {
        Network.post('/onboarding/tag_folders/upsert', { onboarding_tag_folder: folderParams }, {
          success: (data) => { resolve(data); },
          error: (error) => { reject(error); },
        });
      });
    },

    deleteFolder({ commit, dispatch }, { folder }) {
      commit('update', { key: 'formSubmitted', value: true }, { root: true });
      return new Promise((resolve) => {
        Network.delete(`/onboarding/tag_folders/${folder.id}`, {}, {
          success: () => { commit('deleteFolder', { folder }); },
          error: () => { dispatch('setErrorToast', `Could not delete ${folder.name}`, { root: true }); },
          complete: () => {
            resolve();
            commit('update', { key: 'formSubmitted', value: false }, { root: true });
            dispatch('closeModal', null, { root: true });
          },
        });
      });
    },
  },
};
